import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { roleSwitcherLinks } from "@/data/panels";

interface RoleSwitcherProps {
  locked?: boolean;
  roleLabel?: string;
}

export default function RoleSwitcher({ locked = false, roleLabel = "" }: RoleSwitcherProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Özel roller (öğretmen/yönetici): dropdown veya link yok, statik rol rozeti.
  if (locked) {
    return (
      <span className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium text-foreground-500 whitespace-nowrap">
        <i className="ri-shield-star-line text-base text-foreground-400" aria-hidden="true"></i>
        {roleLabel}
      </span>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-10 px-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
        aria-label="Rol değiştir"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <i className="ri-shuffle-line text-base text-foreground-500" aria-hidden="true"></i>
        <span className="hidden md:inline">Rol değiştir</span>
        <i
          className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        ></i>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-1.5 z-50 animate-fade-down"
        >
          <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-foreground-400">
            Rol seç
          </p>
          {roleSwitcherLinks.map((r) => {
            const active = pathname === r.href || pathname.startsWith(`${r.href}/`);
            return (
              <Link
                key={r.href}
                to={r.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-accent-100 text-accent-900 font-semibold"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i
                    className={`${r.icon} text-base ${active ? "text-accent-700" : "text-foreground-400"}`}
                    aria-hidden="true"
                  ></i>
                </span>
                {r.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}